import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { codeTypes, codePaths } from '@/lib/codes'
import { useCodeProfiles } from './composables/useCodeProfiles'
import { useCodesForRepublication } from './composables/useCodesForRepublication'
import { useFilteredCodes } from './composables/useFilteredCodes'
import { useProcessedCodes } from './composables/useProcessedCodes'
import { getCodeIdByPath, getCodeUidByPath } from './codesUtils'
import { apiClientV2, type ApiError } from '@/ContextTab/services/apiClient'
import type { CodeWithCategoryAndChildren } from '@/api/generated/v2'


export const useCodesStore = defineStore('codes', () => {

  const codeTree = ref<CodeWithCategoryAndChildren | Record<string, never>>({})
  const codeErrors = ref<ApiError[] | null>(null)

  const {
    allCodesById,
    allCodesByUid,
    allActualCodesById,
    allActualCodesByUid,
  } = useProcessedCodes(codeTree)

  // Проверяет относится ли код к указанному типу
  function checkCodeIs(codeType: keyof typeof codeTypes, code: any) {
    const prefixes = codeTypes[codeType]
    return prefixes.some((p) => code?.path?.startsWith(p))
  }

  const regionsCodeId = computed(() =>
    getCodeIdByPath(allCodesById, codePaths.regions),
  )

  const regionsCodeUid = computed(() =>
    getCodeUidByPath(newsCodesByUid, codePaths.regions),
  )

  const iptcCodeId = computed(() =>
    getCodeIdByPath(allCodesById, codePaths.iptc),
  )

  const iptcCodeUid = computed(() =>
    getCodeUidByPath(newsCodesByUid, codePaths.iptc),
  )

  const {
    profiles,
    errors: profileErrors,
    fetchCodeProfile,
    isUserHasPublishingCodes,
    publicationCodesById,
    newsCodesById,
    newsCodesByUid,
    newsCodesByIdWithProfileCategory,
    publicationCodesByIdWithProfileCategory,
    publicationCodesByUid,
  } = useCodeProfiles()

  const codesAvailableForRepublicationById = useCodesForRepublication({
    isUserHasPublishingCodes,
    publicationCodes: publicationCodesById,
    newsCodes: newsCodesById,
    iptcCode: iptcCodeId,
    regionsCode: regionsCodeId,
  })

  const codesAvailableForRepublicationByUid = useCodesForRepublication({
    isUserHasPublishingCodes,
    publicationCodes: publicationCodesByUid,
    newsCodes: newsCodesByUid,
    iptcCode: iptcCodeUid,
    regionsCode: regionsCodeUid,
  })

  const feedCodeIds = computed(() => {
    return allCodesById.value
      ? Object.keys(allCodesById.value).filter((key) =>
          checkCodeIs('feed', allCodesById.value[key]),
        )
      : []
  })

  const getIsFeedCode = (code: any) => checkCodeIs('feed', code)

  const { feedAndPub, feedAndPubIds, nonFeed, nonFeedIds } = useFilteredCodes(
    allActualCodesById,
    'id',
  )

  const {
    feedAndPub: feedAndPubByUid,
    feedAndPubIds: feedAndPubUids,
    nonFeed: nonFeedByUid,
    nonFeedIds: nonFeedUids,
  } = useFilteredCodes(allActualCodesByUid, 'uid')

  async function fetchCodes() {
    codeErrors.value = null
    const response = await apiClientV2.CodesService.codesCodesGet()

    if (response.error) {
      codeErrors.value = response.error.errors
    } else if (response.data) {
      codeTree.value = response.data
    }

    return response.data
  }

  const errors = computed(() => ({
    codes: codeErrors.value,
    profile: profileErrors,
  }))

  return {
    codes: allCodesById,
    allCodes: allCodesById,
    allCodesById,
    allCodesByUid,
    actualCodes: allActualCodesById,
    allActualCodesById,
    allActualCodesByUid,
    errors,
    feedCodeIds,
    newsCodesByIdWithProfileCategory,
    publicationCodesByIdWithProfileCategory,

    profiles,
    newsCodesById,
    publicationCodesById,

    codesAvailableForRepublicationById,
    codesAvailableForRepublicationByUid,
    feedAndPub,
    feedAndPubIds,
    nonFeed,
    nonFeedIds,
    feedAndPubByUid,
    feedAndPubUids,
    nonFeedByUid,
    nonFeedUids,

    getIsFeedCode,

    fetchCodes,
    fetchCodeProfile,
  }
})
